import { action, makeObservable, observable } from "mobx";
import { type IssuesStore } from "services/issues/issuesStore";
import { QuestionStoreError, QuestionsStore } from "services/questions/questionsStore";
import { type AbnSoleTraderAnswerTrailEntry, AbnSoleTraderQuestion } from "./abnSoleTraderConstants";
import { ABN_SOLE_TRADER_FLOW_CONFIG, getNextQuestionId, isFlowComplete } from "./abnSoleTraderFlowConfig";

export class AbnSoleTraderQuestionsStore extends QuestionsStore<AbnSoleTraderQuestion> {
  private _answerTrail: AbnSoleTraderAnswerTrailEntry[] = [];
  private _isFlowComplete = false;
  private _formData: Record<string, unknown> = {};

  constructor(protected readonly _issuesStore: IssuesStore) {
    super(AbnSoleTraderQuestion, _issuesStore);

    makeObservable(this, {
      _answerTrail: observable,
      _isFlowComplete: observable,
      _formData: observable,
      addToAnswerTrail: action,
      clearAnswerTrail: action,
      initializeFromJson: action,
      processAnswer: action,
      goNext: action,
      goBack: action,
      setFormData: action,
    } as any);
  }

  get answerTrail(): AbnSoleTraderAnswerTrailEntry[] {
    return this._answerTrail;
  }

  get formData(): Record<string, unknown> {
    return this._formData;
  }

  get isFirstQuestion(): boolean {
    return this.activeQuestionKey === ABN_SOLE_TRADER_FLOW_CONFIG.firstQuestionId;
  }

  get flowComplete(): boolean {
    return this._isFlowComplete;
  }

  setFormData(data: Record<string, unknown>): void {
    this._formData = { ...this._formData, ...data };
  }

  addToAnswerTrail(entry: AbnSoleTraderAnswerTrailEntry): void {
    const currentIndex = this._answerTrail.findIndex((e) => e.questionId === entry.questionId);
    if (currentIndex >= 0) {
      this._answerTrail = this._answerTrail.slice(0, currentIndex);
    }
    this._answerTrail = [...this._answerTrail, entry];
  }

  clearAnswerTrail(): void {
    this._answerTrail = [];
    this._formData = {};
  }

  getNextQuestionId(questionId: string, answerId: string, formData?: Record<string, unknown>): string {
    return getNextQuestionId(questionId, answerId, formData);
  }

  getAnswerTrailAsJson(): string {
    return JSON.stringify({
      answerTrail: this._answerTrail,
      formData: this._formData,
    });
  }

  processAnswer(
    answerId: string,
    formData?: Record<string, unknown>,
    explicitQuestionId?: string,
  ): { isFlowComplete: boolean } {
    // Use explicit questionId if provided to avoid race conditions with activeQuestionKey state changes
    const questionId = explicitQuestionId || this.activeQuestionKey;

    // Merge new formData with existing formData to get complete picture
    const mergedFormData = formData ? { ...this._formData, ...formData } : this._formData;

    // Pass merged formData to determine next question (important for conditional flows)
    const nextQuestionId = this.getNextQuestionId(questionId, answerId, mergedFormData);

    this.addToAnswerTrail({
      questionId,
      answerId,
      nextQuestionId,
    });

    if (formData) {
      this.setFormData(formData);
    }

    if (isFlowComplete(nextQuestionId)) {
      this._isFlowComplete = true;
      return { isFlowComplete: true };
    }

    this._isFlowComplete = false;
    this.goToQuestion(nextQuestionId as AbnSoleTraderQuestion);
    return { isFlowComplete: false };
  }

  goNext(): void {
    if (this._isFlowComplete) {
      throw new QuestionStoreError("ABN flow is complete");
    }
  }

  goBack(): void {
    const currentQuestionId = this.activeQuestionKey;

    const previousEntry = this._answerTrail.find((entry) => entry.nextQuestionId === currentQuestionId);

    if (previousEntry) {
      this.goToQuestion(previousEntry.questionId as AbnSoleTraderQuestion);
    } else if (this.isFirstQuestion) {
      throw new QuestionStoreError("Can't go back from the first question");
    } else {
      this.goToQuestion(ABN_SOLE_TRADER_FLOW_CONFIG.firstQuestionId as AbnSoleTraderQuestion);
    }
  }

  initializeFromJson(json: string | undefined): void {
    if (!json) {
      return;
    }
    try {
      const parsed = JSON.parse(json) as {
        answerTrail: AbnSoleTraderAnswerTrailEntry[];
        formData: Record<string, unknown>;
      };
      this._answerTrail = parsed.answerTrail || [];
      this._formData = parsed.formData || {};

      if (this._answerTrail.length > 0) {
        const lastEntry = this._answerTrail[this._answerTrail.length - 1];
        if (lastEntry && isFlowComplete(lastEntry.nextQuestionId)) {
          this._isFlowComplete = true;
        } else if (lastEntry) {
          this.goToQuestion(lastEntry.nextQuestionId as AbnSoleTraderQuestion);
        }
      }
    } catch (e) {
      console.error("Failed to parse ABN sole trader JSON:", e);
    }
  }

  getAnswerForQuestion(questionId: string): string | undefined {
    const entry = this._answerTrail.find((e) => e.questionId === questionId);
    return entry?.answerId;
  }

  protected _getRelevantQuestion(): AbnSoleTraderQuestion | undefined {
    // If flow is complete, return undefined to let base class use lastQuestion as fallback
    if (this._isFlowComplete) {
      return undefined;
    }

    // If we have an answer trail, return the next question after the last entry
    if (this._answerTrail.length > 0) {
      const lastEntry = this._answerTrail[this._answerTrail.length - 1];
      if (lastEntry && !isFlowComplete(lastEntry.nextQuestionId)) {
        return lastEntry.nextQuestionId as AbnSoleTraderQuestion;
      }
    }

    // Default to first question
    return ABN_SOLE_TRADER_FLOW_CONFIG.firstQuestionId as AbnSoleTraderQuestion;
  }

  protected _shouldSkipQuestion(question: AbnSoleTraderQuestion): boolean {
    if (question === ABN_SOLE_TRADER_FLOW_CONFIG.firstQuestionId) {
      return false;
    }

    const isInAnswerTrail = this._answerTrail.some((entry) => entry.questionId === question);
    return !isInAnswerTrail;
  }
}
